<template>
  <span v-if="bare" class="dx-loader" :class="sizeClass" aria-hidden="true" />

  <div
    v-else
    class="dx-loading"
    :class="[{ 'dx-loading--row': row, 'dx-loading--overlay': overlay, 'dx-loading--fixed': fixed }, wrapperClass]"
    role="status"
    aria-live="polite"
  >
    <span class="dx-loader" :class="sizeClass" aria-hidden="true" />
    <p v-if="resolvedLabel" class="dx-loading__label">{{ resolvedLabel }}</p>
    <p v-if="note" class="dx-loading__note">{{ note }}</p>
    <span v-if="!resolvedLabel" class="visually-hidden">{{ t('common.loading') }}</span>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

/*
 * The only loading component in the application. Its look lives in
 * assets/styles/loader.css, which explains why the colour is inherited from
 * the surrounding theme rather than fixed here.
 *
 * `bare` is the ring alone, for a caller that already owns the announcement —
 * a button with aria-busy, or a row that speaks for itself. Everything else
 * gets role="status", so a screen reader is told the region is loading exactly
 * once, by whichever element is actually responsible for saying so.
 */
const props = defineProps({
  size: {
    type: String,
    default: 'md',
    validator: (v) => ['xs', 'sm', 'md', 'lg', 'xl'].includes(v),
  },
  label: { type: String, default: null },
  note: { type: String, default: '' },
  bare: { type: Boolean, default: false },
  row: { type: Boolean, default: false },
  overlay: { type: Boolean, default: false },
  fixed: { type: Boolean, default: false },
  wrapperClass: { type: [String, Array, Object], default: '' },
  /* Opt out of the default "Loading…" text without losing the announcement. */
  silent: { type: Boolean, default: false },
});

const { t, tm } = useI18n();

const sizeClass = computed(() => (props.size === 'md' ? null : `dx-loader--${props.size}`));

/*
 * One line, chosen once when the loader mounts — deliberately not a rotating
 * ticker. A timer would run for the whole of every wait, re-render on each
 * tick, and tempt the loader into staying up long enough to be read; the point
 * is that the wait ends as soon as the data lands, not that the copy gets seen.
 */
const line = (() => {
  const lines = tm('common.loadingLines');
  if (!Array.isArray(lines) || lines.length === 0) return null;
  const i = Math.floor(Math.random() * lines.length);
  /* tm() hands back raw messages, which are plain strings here but are compiled
   * functions when a message carries interpolation — resolve through t() in
   * that case rather than rendering "[object Object]". */
  return typeof lines[i] === 'string' ? lines[i] : t(`common.loadingLines[${i}]`);
})();

const resolvedLabel = computed(() => {
  if (props.silent) return null;
  if (props.label != null) return props.label;
  return line ?? t('common.loading');
});
</script>
